<template>
  <div class="flex flex-col justify-between flex-1">
    <div class="divide-y divide-gray-200">
      <div class="pt-6 pb-5 space-y-6">
        <Form v-slot="{ values }"
              :initial-values="initial_values"
              :validation-schema="schema"
              class="mt-2 space-y-6"
              @submit="createGoal">
          <div>
            <div class="w-full">
              <div :class="{ 'w-full mb-2': !inPlaybook }">
                <SelectTemplateDropDownMenu
                  v-if="templates.length"
                  v-model="selected_template"
                  :items="templates"
                  label-attr="name"
                  class="relative"
                  :is-box-background="true"
                  input-classes="sm:text-sm appearance-none h-10 font-medium text-gray-700 text-left block w-full px-3 py-2 w-48 rounded-lg shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light "
                  @isEditTemplate="
                    ($event) => {
                      is_edit_template = $event
                    }
                  " />
              </div>
              <div class="flex gap-x-2">
                <div class="w-full mt-1 -space-y-px rounded-md shadow-sm dark:border-dark-divider-border">
                  <TextInput
                    :auto-focus="true"
                    autocomplete="on"
                    :autocomplete-options="resultReferencesListForText"
                    :is-box-background="true"
                    input-classes="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
                    name="name"
                    type="text"
                    :allow-null="false"
                    :value="
                      selected_template?.content?.name && is_edit_template
                        ? selected_template.name
                        : selected_template?.content?.name
                          ? $t('Copy of') + ` ${selected_template.content.name}`
                          : ''
                    "
                    :label="$t('Goal name')" />
                </div>

                <div :class="{ 'h-24 mt-2 w-[800px] ml-20': inPlaybook }" class="w-1/2 h-0 ml-6">
                  <div v-if="isAllRecords" class="whitespace-nowrap text-common-grey mt-2">
                    {{ count - excludedRecords.length }} {{ $t("out of") }} {{ count }} {{ $t("records in table selected") }}
                  </div>
                  <CompaniesSelect
                    v-else
                    :multi-select="inPlaybook ? true : false"
                    :short-king="inPlaybook"
                    :open-position="inPlaybook ? 'right' : 'center'"
                    :label="$t('Add') + ` ${inPlaybook ? $t('Company/Opportunity') : $t('Companies/Opportunities')}`"
                    :companies="selected_companies"
                    :autocomplete-options="resultReferencesList"
                    @changed="
                      ($event) => {
                        handleSetCompaniesList($event)
                      }
                    " />
                </div>
              </div>

              <div class="flex items-center mt-4 gap-x-2">
                <Switch
                  v-model="goal_trigger"
                  :disabled="!selected_companies.length && !isAllRecords"
                  :class="[
                    goal_trigger ? 'bg-blue' : 'bg-gray-200',
                    'relative inline-flex flex-shrink-0 h-7 w-12 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue'
                  ]">
                  <span
                    aria-hidden="true"
                    :class="[
                      goal_trigger && $store.state.portfolio.ui_options?.language == 'ar'
                        ? '-translate-x-5'
                        : goal_trigger
                          ? 'translate-x-5'
                          : 'translate-x-0',
                      'pointer-events-none inline-block h-6 w-6 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
                    ]" />
                </Switch>
                <span class="text-common-grey">{{
                  selected_companies.length || isAllRecords ? $t("Set your goal trigger") : $t("Select company to set your goal trigger")
                }}</span>
              </div>

              <div v-if="goal_trigger && (selected_companies.length || isAllRecords)">
                <AdvancedMetricsFilterForm
                  :object-type="object_type"
                  :initial-filter="selected_template?.content?.filter"
                  @update="handleUpdateFilter" />
              </div>
              <div class="my-4 rounded-md">
                <SelectInput
                  label-attr="label"
                  value-attr="value"
                  :show-label="true"
                  :is-box-background="true"
                  :label="$t('Goal type')"
                  input-classes="appearance-none text-left block w-full border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
                  name="type"
                  :allow-null="false"
                  :value="selected_template?.content?.type"
                  :items="goal_types" />

                <p
                  v-if="goal_types.filter((x) => x.value == values['type']).length > 0"
                  class="p-2 text-sm leading-7 text-left text-gray-500">
                  {{ goal_types.filter((x) => x.value == values["type"])[0]["description"] }}
                </p>
              </div>

              <div class="flex w-full">
                <div class="w-1/2 mt-3 mb-5">
                  <TeamMembersSelect
                    v-model:selected-team-members="selected_users"
                    :autocomplete-options="autocompleteOptionsInPlaybook"
                    :label="$t('Team Members')" />
                </div>
                <div v-if="!inPlaybook" class="w-1/2 mt-3 mb-5">
                  <ContactsSelect v-model:selected-contacts="selected_contacts" :label="$t('Contacts')" :companies="selected_companies" />
                </div>
              </div>

              <div class="mt-5 border rounded-md dark:border-dark-divider-border h-28">
                <TextEditor
                  ref="quill"
                  v-model:content="goal_description"
                  name="description"
                  :label="$t('Description')"
                  content-type="html"
                  input-classes="rounded-md"
                  :value="selected_template?.content?.description"
                  :mention-items="resultReferencesListForText"
                  theme="bubble"
                  toolbar="essential" />
              </div>

              <div class="flex items-center gap-1 w-full mt-4">
                <div class="relative w-full">
                  <label
                    for="start_date"
                    class="absolute z-10 block px-1 text-sm transition-all ease-in-out -translate-y-1/2 rounded-full h-fit dark:text-dark-search-bar-text dark:text-dark-body-text left-2 duration-side-menu-transition-duration"
                    :class="[
                      inputStartDateIsFocused || !!start_date ? 'top-0' : 'top-1/2',
                      isBoxBackgroud
                        ? 'bg-light-box-background dark:bg-dark-box-background'
                        : 'bg-white text-gray-400 dark:bg-dark-box-background',
                      inputStartDateIsFocused ? 'text-light-brand-blue dark:text-dark-brand-blue' : ''
                    ]">{{ $t("Start date") }}</label>
                  <div class="rounded-md shadow-sm">
                    <DatePicker
                      ref="start_date"
                      v-model="start_date"
                      :is-dark="$store.state.ui.darkMode"
                      class="bg-white dark:bg-dark-screen-background"
                      is-inline
                      mode="date"
                      :popover="{ visibility: 'click' }">
                      <template #default="{ inputValue, inputEvents }">
                        <div class="relative">
                          <svg
                            fill="none"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            class="absolute w-4 h-full mx-2 text-gray-600 pointer-events-none">
                            <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>

                          <input
                            id="start_date"
                            autocomplete="off"
                            :value="inputValue"
                            class="w-full h-10 px-10 pl-8 pr-2 transition ease-in-out border rounded-lg outline-none text-light-body-text dark:text-dark-body-text border-light-divider-border dark:border-dark-divider-border focus:ring-1 focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue duration-side-menu-transition-duration"
                            :class="[
                              !isBoxBackgroud
                                ? 'bg-light-box-background dark:bg-dark-box-background'
                                : 'bg-light-screen-background dark:bg-dark-box-background'
                            ]"
                            @focusin="focusStartDateInput"
                            @focusout="focusStartDateInput"
                            v-on="inputEvents"
                            @input="
                              $utils.debounce(() => {
                                start_date = $event.target.value
                              }, 800)
                            ">
                        </div>
                      </template>
                      <template #footer>
                        <div class="flex items-center justify-end pr-2 w-full pb-2 mt-0">
                          <button v-if="start_date" type="button" class="cancel-btn inline-block" @click.stop="emptyStartDate">
                            {{ $t("Clear") }}
                          </button>
                          <button type="button" class="cancel-btn inline-block" @click.stop="clearDate">{{ $t("Cancel") }}</button>
                        </div>
                      </template>
                    </DatePicker>
                  </div>
                </div>
                <div class="relative w-full">
                  <label
                    for="create_goal_d_date"
                    class="absolute z-10 block px-1 text-sm transition-all ease-in-out -translate-y-1/2 rounded-full h-fit dark:text-dark-search-bar-text dark:text-dark-body-text left-2 duration-side-menu-transition-duration"
                    :class="[
                      !!relative_date_text || inputDueDateIsFocused || !!due_date ? 'top-0' : 'top-1/2',
                      isBoxBackgroud
                        ? 'bg-light-box-background dark:bg-dark-box-background'
                        : 'bg-white text-gray-400 dark:bg-dark-box-background',
                      inputDueDateIsFocused ? 'text-light-brand-blue dark:text-dark-brand-blue' : ''
                    ]"
                    @click="is_modal_relative_date_open = true">{{ $t("Due date") }}</label>
                  <div class="rounded-md shadow-sm">
                    <DatePicker
                      ref="due_date"
                      v-model="due_date"
                      :is-dark="$store.state.ui.darkMode"
                      class="bg-white dark:bg-dark-screen-background"
                      is-inline
                      mode="date"
                      :popover="{ visibility: 'none' }">
                      <template #default="{ inputValue, inputEvents }">
                        <div class="relative">
                          <svg
                            fill="none"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            class="absolute w-4 h-full mx-2 text-gray-600 pointer-events-none">
                            <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>

                          <input
                            id="create_goal_d_date"
                            autocomplete="off"
                            :value="inputValue ? inputValue : relative_date_text"
                            class="w-full h-10 px-10 pl-8 pr-2 transition ease-in-out border rounded-lg outline-none text-light-body-text dark:text-dark-body-text border-light-divider-border dark:border-dark-divider-border focus:ring-1 focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue duration-side-menu-transition-duration"
                            :class="[
                              !isBoxBackgroud
                                ? 'bg-light-box-background dark:bg-dark-box-background'
                                : 'bg-light-screen-background dark:bg-dark-box-background'
                            ]"
                            @focusin="focusDueDateInput"
                            @focusout="focusDueDateInput"
                            @click="is_modal_relative_date_open = true"
                            v-on="inputEvents"
                            @input="
                              $utils.debounce(() => {
                                due_date = $event.target.value
                              }, 800)
                            ">
                        </div>
                        <div v-if="is_modal_relative_date_open" class="absolute z-20">
                          <DateTimeFrame
                            class="mr-2 w-full bg-white dark:bg-dark-box-background rounded-lg"
                            @done="
                              ($event) => {
                                due_date = $event
                                is_modal_relative_date_open = false
                              }
                            "
                            @relative-date-changed="
                              ($event) => {
                                relative_date = $event
                                is_modal_relative_date_open = false
                              }
                            "
                            @cancel="is_modal_relative_date_open = false" />
                        </div>
                      </template>

                      <template #footer>
                        <div class="flex items-center justify-end pr-2 w-full pb-2 mt-0">
                          <button v-if="due_date || relative_date" type="button" class="cancel-btn inline-block" @click.stop="emptyDueDate">
                            {{ $t("Clear") }}
                          </button>
                        </div>
                      </template>
                    </DatePicker>
                  </div>
                </div>
              </div>

              <div :class="[{ '!mt-6': inPlaybook }]" class="flex items-center mr-6 gap-x-2 !mt-4 mb-4">
                <Switch
                  v-model="auto_set_start_date"
                  :class="[
                    auto_set_start_date ? 'bg-blue' : 'bg-gray-200',
                    'relative inline-flex flex-shrink-0 h-7 w-12 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue'
                  ]">
                  <span
                    aria-hidden="true"
                    :class="[
                      auto_set_start_date && $store.state.portfolio.ui_options?.language == 'ar'
                        ? '-translate-x-5'
                        : auto_set_start_date
                          ? 'translate-x-5'
                          : 'translate-x-0',
                      'pointer-events-none inline-block h-6 w-6 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
                    ]" />
                </Switch>
                <span class="dark:text-common-grey">{{ $t("Auto Set Start Date") }}</span>
              </div>

              <div class="cursor-pointer" @click="assign_companies_to_tasks = !assign_companies_to_tasks">
                <span class="mr-2 mt-2 text-common-grey">{{ $t("Assign selected companies to tasks") }} </span>
                <input
                  :disabled="true"
                  :checked="assign_companies_to_tasks"
                  type="checkbox"
                  class="form-checkbox pointer-events-none text-light-brand-blue dark:text-dark-brand-blue checked:bg-light-brand-blue dark:checked:bg-dark-brand-blue bg-light-box-background dark:bg-dark-box-background border-[2px] focus:ring-0 ring-dark/0 border-common-400 w-5 rounded h-5 transition ease-in-out duration-side-menu-transition-duration">
              </div>

              <div class="cursor-pointer" @click="assign_collaborators_to_tasks = !assign_collaborators_to_tasks">
                <span class="mt-1 mr-2 text-common-grey">{{ $t("Assign selected team members to tasks") }} </span>
                <input
                  :disabled="true"
                  :checked="assign_collaborators_to_tasks"
                  type="checkbox"
                  class="form-checkbox pointer-events-none text-light-brand-blue dark:text-dark-brand-blue checked:bg-light-brand-blue dark:checked:bg-dark-brand-blue bg-light-box-background dark:bg-dark-box-background border-[2px] focus:ring-0 ring-dark/0 border-common-400 w-5 rounded h-5 transition ease-in-out duration-side-menu-transition-duration">
              </div>

              <div class="flex items-center mt-4 gap-x-2">
                <label class="flex items-baseline justify-between font-medium text-gray-500 text-md">{{ $t("Assigned tasks") }}:</label>

                <div
                  v-if="!isEditTemplate && !inPlaybook"
                  class="btn ml-auto w-fit primary-btn"
                  @click="!isEditTemplate && !inPlaybook ? handleCreateTask() : null">
                  {{ $t("Create New Task") }}
                </div>

                <Menu v-else
                      v-slot="{ close }"
                      ref="goalsMenu"
                      as="div"
                      class="relative flex-shrink-0 ml-auto">
                  <MenuButton
                    class="inline-flex items-center justify-center rounded-full w-36 border-1 focus:outline-none focus:ring-1 focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue">
                    <div class="btn primary-btn">
                      {{ $t("Add Task to Goal") }}
                    </div>
                  </MenuButton>
                  <transition
                    enter-active-class="transition duration-100 ease-out"
                    enter-from-class="transform scale-95 opacity-0"
                    enter-to-class="transform scale-100 opacity-100"
                    leave-active-class="transition duration-75 ease-in"
                    leave-from-class="transform scale-100 opacity-100"
                    leave-to-class="transform scale-95 opacity-0">
                    <MenuItems class="absolute z-50 w-40 mx-3 mt-3 -ml-48 origin-bottom-right bg-transparent border-0 focus:outline-none">
                      <div
                        class="py-1 bg-white p-3 border dark:border-dark-divider-border dark:bg-dark-screen-background rounded-lg w-[340px]">
                        <div>
                          <div v-if="selected_template" class="flex w-full justify-end">
                            <div
                              :class="{ 'text-white bg-light-brand-blue': list_to_get == 'tasks' }"
                              class="p-1 px-3 rounded-full border cursor-pointer"
                              @click="list_to_get = 'tasks'">
                              {{ $t("Tasks") }}
                            </div>
                            <div
                              :class="{ 'text-white bg-light-brand-blue': list_to_get == 'templates' }"
                              class="p-1 px-3 rounded-full border ml-2 cursor-pointer"
                              @click="list_to_get = 'templates'">
                              {{ $t("Templates") }}
                            </div>
                          </div>

                          <AsyncAutoCompleteInput
                            :add="false"
                            :add-label="$t('Create Task')"
                            :selected-items="selected_tasks"
                            :clear-on-select="true"
                            :is-box-background="true"
                            classes="w-full"
                            input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm "
                            name="task"
                            value-attr="id"
                            :endpoint="
                              list_to_get == 'tasks'
                                ? `/api/organizations/${$store.state.organization.id}/tasks/`
                                : `/api/organizations/${$store.state.organization.id}/templates?source_object_type=goal`
                            "
                            type="text"
                            :placeholder="$t('Search for task to add')"
                            :debounce="500"
                            @chosen="
                              ($event) => {
                                handleTaskSelected($event, close)
                              }
                            " />
                        </div>
                        <div
                          v-if="!inPlaybook"
                          class="mt-3 mb-3 btn primary-btn"
                          @click="
                            () => {
                              close()
                              handleCreateTask()
                            }
                          ">
                          + {{ $t("Create New Task") }}
                        </div>
                      </div>
                    </MenuItems>
                  </transition>
                </Menu>
              </div>

              <div
                v-if="!tasksCurrentPageItems.length"
                class="relative w-full p-4 mb-4 text-center border rounded-lg top-4 dark:border-dark-divider-border text-common-grey">
                <div class="text-lg">
                  {{ $t("No tasks to display") }}
                </div>
                <div class="mt-1 text-sm">
                  {{ $t("Add new tasks to goal") }}
                </div>
              </div>

              <draggable
                v-model="selected_tasks"
                as="div"
                static
                :class="[
                  'bg-white dark:border-dark-divider-border border-white dark:bg-dark-box-background py-4 px-2',
                  `grid grid-cols-1 gap-x-8 overflow-visible mt-1 border-0 ${
                    $store.state.ui.darkMode ? 'dark-scrollbar' : ''
                  } dark:border-dark-divider-border rounded-lg text-base ring-0 overflow-auto focus:outline-none sm:text-sm transition ease-in-out duration-side-menu-transition-duration`
                ]"
                :component-data="{ type: 'transition-group' }"
                v-bind="{
                  disabled: tasksCurrentPageItems?.length < selected_tasks?.length || tasksCurrentPageItems < 2,
                  animation: 200,
                  group: 'group',
                  ghostClass: 'ghost',
                  chosenClass: 'chosen'
                }"
                item-key="id">
                <template #item="{ element: task }">
                  <div class="bg-white cursor-pointer">
                    <div
                      v-if="expanded_tasks.includes(task.id)"
                      class="relative p-4 mb-4 border rounded-md dark:bg-dark-screen-background dark:border-dark-divider-border dark:text-white"
                      @mouseenter="handleTaskEnter(task)"
                      @mouseleave="handleTaskLeave">
                      <div class="flex items-center">
                        <ToggleStatusButton
                          :prevent-request="isEditTemplate"
                          :object="task"
                          @statusChanged="
                            ($event) => {
                              handleStatusChanged($event, task)
                            }
                          " />
                        <div class="ml-2">
                          {{ task.name }}
                        </div>
                        <div class="absolute flex -right-3 -top-3 gap-x-2">
                          <div
                            class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                            @click="handleExpandTask(task)">
                            <div class="w-[15px] h-[15px] ml-[5px] mt-[0px] border-b-[3px] border-b-[#45D72D]" />
                            <span
                              class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                              {{ $t("Minimize") }}</span>
                          </div>
                          <div
                            class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:bg-dark-screen-background dark:border-dark-divider-border"
                            @click="handleViewTask(task)">
                            <Maximize2
                              class="w-5 h-5 ml-[3px] mt-[3px] transition ease-in-out duration-side-menu-transition-duration"
                              color="#2DA7D7" />
                            <span
                              class="tooltip whitespace-nowrap inline-flex items-center px-2 py-1 rounded-md text-sm w-[80px] bg-light-screen-background dark:bg-dark-screen-background font-medium dark:text-dark-body-text gap-2">
                              {{ $t("View Task") }}</span>
                          </div>
                          <div
                            class="flex items-center justify-center bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                            @click="handleRemoveTask(task)">
                            <X color="#FF0000" class="w-5 h-5 transition ease-in-out duration-side-menu-transition-duration" /><span
                              class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                              {{ $t("Remove") }}</span>
                          </div>
                        </div>
                      </div>
                      <div class="flex w-full mt-5">
                        <div class="w-1/3">
                          <div class="flex flex-row items-center gap-2">
                            <div class="text-common-grey">
                              {{ $t("Priority") }}:
                            </div>

                            <div class="flex items-start justify-start w-1/12 py-2 text-start whitespace-nowrap text-md">
                              <div v-if="task.priority" class="flex items-center capitalize gap-x-2">
                                <div>
                                  {{ task.priority }}
                                </div>
                                <div>
                                  <div v-if="task.priority == 'normal'">
                                    <div :class="[`border-b-2 w-3  border-status-warning-yellow`]" />
                                    <div :class="[`border-b-2 w-3 mt-1 border-status-warning-yellow`]" />
                                  </div>
                                  <div v-else-if="task.priority == 'high'">
                                    <ChevronRight class="w-5 h-5 -rotate-90 text-status-error-red" />
                                  </div>
                                  <div v-else-if="task.priority == 'highest'">
                                    <ChevronsRight class="w-5 h-5 -rotate-90 text-status-error-red" />
                                  </div>
                                  <div v-else-if="task.priority == 'low'">
                                    <ChevronRight class="w-5 h-5 rotate-90 text-status-success-green" />
                                  </div>
                                  <div v-else-if="task.priority == 'lowest'">
                                    <ChevronsRight class="w-5 h-5 rotate-90 text-status-success-green" />
                                  </div>
                                </div>
                              </div>

                              <span v-else> - </span>
                            </div>
                          </div>
                        </div>

                        <div class="w-1/3">
                          <div class="flex flex-row items-center gap-2">
                            <div class="text-common-grey whitespace-nowrap">
                              {{ $t("Due Date") }}:
                            </div>

                            <div class="flex items-start justify-start w-1/12 py-2 text-start whitespace-nowrap text-md">
                              <span v-if="task.due_date">
                                {{ $moment(task.due_date).format("DD MMM  YYYY") }}
                              </span>

                              <span v-else> - </span>
                            </div>
                          </div>
                        </div>

                        <div :class="[`${task.assigned_to?.length > 4 ? 'w-[16%]' : ''}`]" class="ml-auto -mt-8">
                          <div
                            :class="[`${task.assigned_to?.length > 4 ? '' : '-ml-10'}`]"
                            class="mb-2 text-common-grey whitespace-nowrap text-start">
                            {{ $t("Team Members") }}
                          </div>
                          <div class="flex items-center -ml-5 -space-x-4 overflow-hidden">
                            <span v-for="user in task.assigned_to?.slice(0, 4)" :key="user" class="inline-block has-tooltip">
                              <img
                                v-if="$utils.getUserById(user, 'avatar')"
                                class="inline-block rounded-full h-9 w-9 object-cover"
                                :src="$utils.getUserById(user, 'avatar')"
                                alt="">
                              <Avatar
                                v-else
                                :first-name="$utils.getUserById(user, 'first_name')"
                                :last-name="$utils.getUserById(user, 'last_name')" />

                              <span class="p-1 bg-gray-300 rounded-sm tooltip whitespace-nowrap">
                                <div class="">
                                  <p class="text-sm font-medium dark:!text-white text-gray-700 group-hover:text-gray-900">
                                    {{ $utils.getUserById(user, "full_name") }}
                                  </p>
                                </div>
                              </span>
                            </span>
                            <span
                              v-if="task.assigned_to?.length > 4"
                              class="relative ml-auto text-sm left-6 text-light-brand-blue dark:text-dark-brand-blue">
                              + {{ task.assigned_to?.length - 4 }} {{ $t("more") }}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div
                      v-else
                      class="relative flex p-4 mb-4 align-middle border rounded-md dark:bg-dark-screen-background dark:border-dark-divider-border dark:text-white"
                      @mouseenter="handleTaskEnter(task)"
                      @mouseleave="handleTaskLeave">
                      <div
                        :class="[
                          'w-4 h-4 rounded-full mt-1 mr-4 relative',
                          {
                            'bg-status-warning-yellow': getActualStatus(task, task.status) == 'overdue',
                            'bg-dark-brand-blue': ['in-progress', 'pending'].includes(task.status),

                            'bg-status-success-green': task.status == 'completed',
                            'bg-status-error-red': ['cancelled', 'missed'].includes(task.status)
                          }
                        ]" />
                      <Tally2
                        v-if="tasksCurrentPageItems.length == selected_tasks.length && tasksCurrentPageItems.length > 1"
                        class="h-5 w-5 mr-2 text-common-grey rotate-90"
                        aria-hidden="true" />

                      <div>{{ task.name }}</div>

                      <div class="absolute flex -right-3 -top-3 gap-x-2">
                        <div
                          class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                          @click="handleExpandTask(task)">
                          <div class="w-[15px] h-[15px] ml-[5px] mt-[5px] border-[3px] rounded-md border-[#45D72D]" />
                          <span
                            class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                            {{ $t("Expand") }}</span>
                        </div>
                        <div
                          class="bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:bg-dark-screen-background dark:border-dark-divider-border"
                          @click="handleViewTask(task)">
                          <Maximize2
                            class="w-5 h-5 ml-[3px] mt-[3px] transition ease-in-out duration-side-menu-transition-duration"
                            color="#2DA7D7" />
                          <span
                            class="tooltip whitespace-nowrap inline-flex items-center px-2 py-1 rounded-md text-sm w-[80px] bg-light-screen-background dark:bg-dark-screen-background font-medium dark:text-dark-body-text gap-2">
                            {{ $t("View Task") }}</span>
                        </div>
                        <div
                          class="flex items-center justify-center bg-white border rounded-full cursor-pointer has-tooltip w-7 h-7 dark:border-dark-divider-border dark:bg-dark-screen-background"
                          @click="handleRemoveTask(task)">
                          <X color="#FF0000" class="w-5 h-5 transition ease-in-out duration-side-menu-transition-duration" /><span
                            class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
                            {{ $t("Remove") }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </draggable>

              <div class="flex items-center w-full">
                <div v-if="selected_tasks.length > 6" class="mr-2 text-sm text-common-grey">
                  {{ $t("Show") }}
                </div>
                <SelectInput
                  v-if="selected_tasks.length > 6"
                  :value="tasksPerPage"
                  :is-box-background="true"
                  class="w-[115px]"
                  :input-classes="'text-left'"
                  name="tasks_page_show_options"
                  :allow-null="false"
                  :items="tasks_page_show_options"
                  @update:currentItem="tasksPerPage = $event" />
                <div v-if="selected_tasks.length > 6" class="ml-2 text-sm text-common-grey">
                  {{ $t("in page") }}
                </div>
                <Pagination
                  v-if="selected_tasks.length > tasksPerPage"
                  class="ml-auto"
                  :total-pages="tasksTotalPages"
                  :current-page="tasksCurrentPage"
                  @input="tasksChangePage" />
              </div>

              <div class="relative rounded-md shadow-sm border p-2 mt-2">
                <div class="flex items-center justify-between w-full">
                  <label class="block text-common-grey">{{ $t("Tags") }}</label>
                  <button class="primary-btn w-fit" @click.prevent="is_tags_modal_open = true">
                    <Plus class="w-6 h-6 mr-2" aria-hidden="true" />
                    <span>{{ selected_tags.length ? $t("Edit Tags") : $t("Add Tags") }}</span>
                  </button>
                </div>
                <div v-if="selected_tags.length" class="flex flex-wrap gap-2 mt-4">
                  <div
                    v-for="tag in selected_tags"
                    :key="tag?.id"
                    class="flex focus:outline-none rounded-md"
                    :style="{ background: tag.color }">
                    <div v-if="selected_tags.find((t) => t.id == tag.id)" class="flex items-center group">
                      <span
                        class="flex items-center rounded-md px-2 py-1.5 border border-gray-300"
                        :style="{
                          background: tag.color
                        }">
                        <span class="block text-white truncate">{{ tag.name }}</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <AttachmentsField v-model="attachments" class="mt-4 mb-6" />

              <div class="flex flex-row justify-between w-full gap-2 mt-3">
                <button
                  type="button"
                  class="w-1/2 m-0 cancel-btn border-light-divider-border dark:border-dark-divider-border"
                  @click="handleCloseGoal()">
                  {{ $t("Cancel") }}
                </button>
                <button v-if="!is_edit_template" :disabled="submitting_is_on" type="submit" class="w-1/2 primary-btn">
                  <RotateCw
                    :class="{
                      'animate-spin': submitting_is_on,
                      'opacity-100': submitting_is_on
                    }"
                    class="w-5 h-5 mr-3 -ml-1 opacity-0"
                    aria-hidden="true" />
                  <span class="mr-5 whitespace-nowrap"> {{ inPlaybook ? $t("Save") : $t("Create") }} {{ $t("Goal") }} </span>
                </button>
                <button v-else :disabled="submitting_is_on" type="submit" class="w-1/2 primary-btn">
                  <RotateCw
                    :class="{
                      'animate-spin': submitting_is_on,
                      'opacity-100': submitting_is_on
                    }"
                    class="w-5 h-5 mr-3 -ml-1 opacity-0"
                    aria-hidden="true" />
                  <span class="mr-5 whitespace-nowrap">{{ $t("Update template") }}</span>
                </button>
              </div>
            </div>
          </div>
        </Form>
      </div>
    </div>
    <!-- Assign companies to Goal taksk -->
    <Modal v-model:is-open="assign_company_to_tasks_modal" default-success-msg="" :use-toast="false">
      <template #form="{ close: closeModal }">
        <h3 class="text-2xl font-medium leading-10 text-gray-800 dark:text-dark-body-text">
          {{ $t("Create Tasks for selected Company?") }}
        </h3>
        <div>
          {{ $t("Do you also want to create this Goal's") }}
          {{ selected_tasks.length > 1 ? $t("Tasks") : $t("Task") }} {{ $t("for this company?") }}
        </div>
        <div class="flex w-full mt-10">
          <button type="button" class="w-full mr-4 btn primary-btn" @click.prevent="handleAssignConpanyToTasks(closeModal)">
            {{ $t("Yes") }}
          </button>
          <button type="button" class="w-full btn cancel-btn" @click="closeModal">{{ $t("No") }}</button>
        </div>
      </template>
    </Modal>

    <!-- Add tags -->
    <Modal v-model:is-open="is_tags_modal_open" :default-success-msg="$t('Note Deleted')">
      <template #form="{ close: closeModal }">
        <TagsAutoCompleteInput
          :placeholder="$t('Add new tag')"
          value-attr="full_name"
          :selected-tags="selected_tags"
          @changed="
            ($event) => {
              handleTagsChanged($event)
            }
          "
          @close="closeModal" />
      </template>
    </Modal>
  </div>
</template>

<script>
import * as Yup from "yup"
import { DatePicker } from "v-calendar"
import SelectTemplateDropDownMenu from "@/components/shared/SelectTemplateDropDownMenu.vue"
import { Switch } from "@headlessui/vue"
import Modal from "@/components/shared/Modal.vue"
import SelectInput from "@/components/shared/SelectInput.vue"
import TextEditor from "@/components/shared/TextEditor.vue"
import TagsAutoCompleteInput from "@/components/shared/TagsAutoCompleteInput.vue"
import TextInput from "@/components/shared/TextInput.vue"
import { Form } from "vee-validate"
import { event as google_event } from "vue-gtag-next"
import Avatar from "@/components/shared/Avatar.vue"
import ContactsSelect from "@/components/shared/ContactsSelect.vue"
import TeamMembersSelect from "@/components/shared/TeamMembersSelect.vue"
import CompaniesSelect from "@/components/shared/CompaniesSelect.vue"
import dataService from "@/services/data.service"
import { Menu, MenuButton, MenuItems } from "@headlessui/vue"
import AsyncAutoCompleteInput from "@/components/shared/AsyncAutoCompleteInput.vue"
import DateTimeFrame from "@/components/shared/DateTimeFrame.vue"
import { Maximize2, ChevronsRight, ChevronRight, Tally2, Plus, RotateCw, X } from "lucide-vue-next"
import ToggleStatusButton from "../shared/ToggleStatusButton.vue"
import Pagination from "@/components/shared/Pagination.vue"
import AttachmentsField from "@/components/shared/form-inputs/AttachmentsField.vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import AdvancedMetricsFilterForm from "@/components/forms/AdvancedMetricsFilterForm.vue"
import { transformMetricFilterToServerFilter } from "@/services/data.transformers"
import draggable from "vuedraggable"
import { useI18n } from "vue-i18n"

export default {
  components: {
    Avatar,
    Tally2,
    draggable,
    Plus,
    TagsAutoCompleteInput,
    TextEditor,
    Modal,
    SelectTemplateDropDownMenu,
    DateTimeFrame,
    AdvancedMetricsFilterForm,
    CompaniesSelect,
    AttachmentsField,
    Pagination,
    ChevronRight,
    ChevronsRight,
    ToggleStatusButton,
    X,
    Maximize2,
    AsyncAutoCompleteInput,
    Menu,
    MenuButton,
    MenuItems,
    TextInput,
    Switch,
    ContactsSelect,
    TeamMembersSelect,
    SelectInput,
    Form,
    RotateCw,
    DatePicker
  },
  props: {
    isEditTemplate: {
      type: Boolean,
      default: false
    },
    selectedTemplate: {
      type: Object,
      default: null
    },
    resultReferencesList: null,
    resultReferencesListForText: null,
    autocompleteOptionsInPlaybook: {
      type: Array,
      required: false,
      default: () => []
    },
    inPlaybook: {
      type: Boolean,
      default: false
    },
    count: null,
    records: {
      type: Array,
      required: false
    },

    record: null,
    isInPlan: {
      type: Boolean,
      required: false
    },
    isInMilestone: {
      type: Boolean,
      required: false
    },
    contact: {
      type: Object,
      required: false
    },
    isAllRecords: {
      type: Boolean,
      default: false
    },
    excludedRecords: {
      type: Array,
      default: () => []
    },
    objectType: {
      type: String,
      required: false
    }
    // autocompleteOptions: {
    //   type: Array,
    //   required: false
    // }
  },
  emits: ["cancel", "submitted", "error", "done"],
  setup() {
    const { t } = useI18n()
    const schema = Yup.object().shape({
      name: Yup.string().required(t("Goal name is required")),
      description: Yup.string(),
      type: Yup.string().required(t("Goal type is required")).nullable(),
      assigned_to: Yup.array().of(Yup.string())
    })
    const initial_values = { name: "", description: "", assigned_to: [] }

    return {
      schema,
      initial_values
    }
  },

  data() {
    return {
      list_to_get: "tasks",
      auto_set_start_date: false,
      selected_tags: [],
      all_tags: [],
      is_tags_modal_open: false,
      assign_companies_to_tasks: false,
      assign_collaborators_to_tasks: false,
      is_edit_template: false,
      attachments: [],
      isBoxBackgroud: false,
      goal_trigger: false,
      selected_users: [],
      selected_contacts: [],
      selected_companies: [],
      selected_template: null,
      templates: [],
      goal_description: null,
      due_date: null,
      relative_date: null,
      relative_date_text: null,
      start_date: null,
      inputDueDateIsFocused: false,
      inputStartDateIsFocused: false,
      is_modal_relative_date_open: false,
      assign_company_to_tasks_modal: false,
      is_filter_valid: false,
      submitting_is_on: false,
      filter: null,
      object_type: "company",
      goal_types: [
        {
          label: this.$t("Specific"),
          value: "specific",
          description: this.$t("Set a specific goal such as “Book an upsell meeting with the decision maker and team at this record.”")
        },
        {
          label: this.$t("Measurable"),
          value: "measurable",
          description: this.$t("Set a measurable such as “Increase ARR with 100k.“")
        },
        {
          label: this.$t("Time-Bound"),
          value: "time-Bound",
          description: this.$t("Set a time-bound goal such as “Q4 100k expansion goal.“")
        }
      ],
      selected_tasks: [],
      created_tasks: [],
      tasksCurrentPage: 1,
      tasksPerPage: 6,
      task_hover_id: null,
      expanded_tasks: [],
      object_type_fetched: false
    }
  },

  computed: {
    tasks_page_show_options() {
      const options = [6]
      if (this.selected_tasks.length > 6) {
        options.push(8)
      }
      if (this.selected_tasks.length > 8) {
        options.push(10)
      }
      if (this.selected_tasks.length > 10) {
        options.push(12)
      }
      return options
    },
    tasksCurrentPageItems() {
      const start = (this.tasksCurrentPage - 1) * this.tasksPerPage
      return this.selected_tasks.slice(start, start + this.tasksPerPage)
    },
    tasksTotalPages() {
      return Math.ceil(this.selected_tasks.length / this.tasksPerPage)
    }
  },

  watch: {
    tasksPerPage: {
      handler() {
        this.tasksCurrentPage = 1
      }
    },
    relative_date() {
      if (this.relative_date) {
        if (this.relative_date.due_date_relative_to == "datetime_created") {
          this.relative_date_text = `${this.relative_date.due_date_days_offset} ` + this.$t("days after Creation Date")
        } else if (this.relative_date.due_date_relative_to == "start_date") {
          this.relative_date_text = `${this.relative_date.due_date_days_offset} ` + this.$t("days after Start Date")
        } else {
          this.relative_date_text = `${this.relative_date.due_date_days_offset} ` + this.$t("days from In Progress")
        }
        this.due_date = null
      }
    },
    due_date() {
      if (this.due_date) {
        this.relative_date_text = null
      }
    },
    selected_template() {
      this.relative_date_text = null
      if (this.selected_template?.content?.due_date_relative_to) {
        this.relative_date = {
          due_date_relative_to: this.selected_template.content.due_date_relative_to,
          due_date_days_offset: this.selected_template.content.due_date_days_offset
        }
      } else {
        this.due_date = this.selected_template?.content?.due_date
      }
      this.start_date = this.selected_template?.content?.start_date
      if (this.selected_template?.content?.attachments?.length) {
        this.attachments = this.selected_template?.content?.attachments
      }

      if (this.selected_template?.content?.auto_start_date_set) {
        this.auto_set_start_date = this.selected_template?.content?.auto_start_date_set

        if (this.selected_template?.content.status == "in-progress" && !this.selected_template?.content.start_date) {
          this.start_date = new Date()
        }
      }

      if (this.selected_template?.content?.tags && this.selected_template?.content?.tags.length) {
        this.selected_tags = this.$store.state.data.actions_tags.filter((tag) => this.selected_template.content.tags.includes(tag.id))
      }

      if (this.selected_template?.content?.assigned_to?.length) {
        this.selected_users = this.selected_template?.content?.assigned_to
      }

      this.selected_template?.content?.tasks?.map((task) => {
        task["assigned_to"] = this.selected_users.map((x) => (x.id ? x.id : x))
        delete task?.attachments
        delete task?.notes
        task["id"] = this.$uuid()
        task["for_template"] = true
        this.selected_tasks.push(task)
      })

      if (this.selected_template?.content?.record) {
        if (this.selected_template?.content?.record.id) {
          this.selected_companies.push(this.selected_template?.content?.record)
        } else {
          this.$store.dispatch("data/getRecord", this.selected_template?.content?.record).then((res) => {
            this.selected_companies.push(res.data)
          })
        }
      }
      if (this.selected_template?.content?.contacts?.length) {
        this.selected_template?.content?.contacts.forEach((c) => {
          if (c.id) {
            this.selected_contacts.push(c)
          } else {
            this.$store.dispatch("data/getRecord", c).then((res) => {
              this.selected_contacts.push(res.data)
            })
          }
        })
      }
      this.goal_description = this.selected_template?.content?.description
    }
  },
  created() {
    if (this.selectedTemplate && this.isEditTemplate) {
      this.is_edit_template = true
      this.selected_template = this.selectedTemplate
      this.selectedTemplate.content.tasks?.forEach((task) => {
        task["id"] = this.$uuid()
        task["for_template"] = true
      })
    } else if (this.selectedTemplate) {
      this.is_edit_template = false
      this.selected_template = this.selectedTemplate
    }
    const params = { source_object_type: "goal" }
    dataService.getTemplates(params).then((res) => {
      this.templates = res.data.results
    })
    this.register_listeners()

    if (!this.isEditTemplate) {
      this.selected_users.push(this.$store.state.auth.user)
    }
    this.selected_contacts = this.contact ? [this.contact] : []

    if (this.$route.name !== "WorkspaceItem") {
      this.records?.slice(0, 20)?.forEach((rec) => {
        if (rec?.id) {
          this.selected_companies.push(rec)
          if (!this.object_type_fetched) {
            this.object_type = rec.type
            this.object_type_fetched = true
          }
        } else {
          if (this.$store.state.data.records_fetched[rec]) {
            this.selected_companies.push(this.$store.state.data.records_fetched[rec])

            if (!this.object_type_fetched) {
              this.object_type = rec.type
              this.object_type_fetched = true
            }
          } else {
            if (this.$store.state.data.records_fetched[rec]) {
              this.selected_companies.push(this.$store.state.data.records_fetched[rec])

              if (!this.object_type_fetched) {
                this.object_type = rec.type
                this.object_type_fetched = true
              }
            } else {
              if (this.$store.state.data.records_fetched[rec]) {
                this.selected_companies.push(this.$store.state.data.records_fetched[rec])

                if (!this.object_type_fetched) {
                  this.object_type = this.$store.state.data.records_fetched[rec].type
                  this.object_type_fetched = true
                }
                this.selected_companies.push(this.$store.state.data.records_fetched[rec])
              } else {
                this.$store.dispatch("data/getRecord", rec).then((res) => {
                  if (!this.object_type_fetched) {
                    this.object_type = res.data.type
                    this.object_type_fetched = true
                  }
                  this.selected_companies.push(res.data)
                })
              }
            }
          }
        }
      })
    }
    if (this.record && this.$route.name !== "WorkspaceItem") {
      if (this.record.id) {
        this.selected_companies.push(this.record)
        this.object_type = this.record.type
      } else {
        if (this.$store.state.data.records_fetched[this.record]) {
          this.selected_companies.push(this.$store.state.data.records_fetched[this.record])
          this.object_type = this.$store.state.data.records_fetched[this.record].type
        } else {
          this.$store.dispatch("data/getRecord", this.record).then((res) => {
            this.selected_companies.push(res.data)
            this.object_type = res.data.type
          })
        }
      }
    }

    if (this.$route.name == "WorkspaceItem") {
      this.$store.dispatch("data/getRecord", this.$store.state.ui.currentWorkspace.record).then((res) => {
        this.selected_companies.push(res.data)
        this.object_type = res.data.type
      })
    }
  },

  activated() {
    this.register_listeners()
  },
  unmounted() {
    this.unregister_listeners()
  },
  deactivated() {
    this.unregister_listeners()
  },
  mounted() {
    this.object_type = this.selected_companies[0] ? this.selected_companies[0].type : null
    if (this.objectType) {
      this.object_type = this.objectType
    }
  },

  methods: {
    emptyStartDate() {
      this.start_date = null
      this.$refs.start_date.hidePopover()
    },
    emptyDueDate() {
      this.due_date = null
      this.relative_date = null
      this.relative_date_text = null
      this.$refs.due_date.hidePopover()
      this.is_modal_relative_date_open = false
    },
    getActualStatus(object, status) {
      if (object.due_date) {
        if (
          (status == "pending" || status == "in-progress") &&
          this.$moment(object.due_date).local().startOf("day").isBefore(this.$moment().startOf("day"))
        ) {
          return "overdue"
        }
        return status
      }
      return status
    },
    handleStatusChanged(status, item) {
      item.status = status
    },
    handleTagsChanged(tags) {
      this.all_tags = this.$store.state.data.actions_tags
      this.selected_tags = tags
    },
    handleCloseGoal() {
      this.$emit("cancel")
    },
    handleUpdateFilter(filter) {
      const filter_cleaned = []
      filter.forEach((item) => {
        if (item.data_type == "String") {
          if (Array.isArray(item.value)) {
            item.value = String(item.value)
          }
          // let value = item.value.replace(/[']+/g, "")
          filter_cleaned.push({
            ...item,
            value: item.value == null ? "" : item.value.trim()
          })
        } else {
          if (
            item.value == null ||
            item.value == undefined ||
            item.value == "" ||
            item.value == "null" ||
            item.value == "undefined" ||
            item.value == "None"
          ) {
            item.value = null
          } else if (
            (item.data_type == "Date" || item.data_type == "DateTime") &&
            !(
              item.operator.value == "__DAYS_WITHIN_LAST_X_DAYS__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_AGO__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_AGO__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_FROM_NOW__" ||
              item.operator.value == "__DAYS_WITHIN_NEXT_X_DAYS__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_FROM_NOW__"
            )
          ) {
            item.value = this.$moment(item.value).format("YYYY-MM-DD")
          } else if (
            item.data_type == "DateTime" &&
            !(
              item.operator.value == "__DAYS_WITHIN_LAST_X_DAYS__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_AGO__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_AGO__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_FROM_NOW__" ||
              item.operator.value == "__DAYS_WITHIN_NEXT_X_DAYS__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_FROM_NOW__"
            )
          ) {
            item.value = this.$moment(item.value).format("YYYY-MM-DDTHH:mm:ss")
          }
          filter_cleaned.push({ ...item, value: item.value })
        }
      })
      let parsed_filter = transformMetricFilterToServerFilter([...filter_cleaned])

      this.is_filter_valid = parsed_filter.valid
      this.filter = parsed_filter.filter
    },

    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.ADD_SELECTED_TASKS_TO_GOAL_CREATE_FORM, (tasks) => {
        this.selected_tasks = tasks.concat(this.selected_tasks)
        this.tasksCurrentPage = 1
      })

      this.$emitter.on(ALL_EMITTERS.TASK_UPDATED_IN_GOAL_CREATE_FORM, (task) => {
        let index = this.selected_tasks.findIndex((g) => g.id == task.id)
        this.selected_tasks.splice(index, 1, task)
      })
    },
    unregister_listeners() {
      this.$emitter.off(ALL_EMITTERS.ADD_SELECTED_TASKS_TO_GOAL_CREATE_FORM)
      this.$emitter.off(ALL_EMITTERS.TASK_UPDATED_IN_GOAL_CREATE_FORM)
    },
    handleSetCompaniesList(e) {
      this.selected_companies = e
      if (!e.length) {
        this.goal_trigger = false
      }
      if (this.selected_companies?.length && this.selected_tasks.length && !this.inPlaybook) {
        //this.assign_company_to_tasks_modal = true
      }
      if (this.selected_companies.length) {
        this.object_type = this.selected_companies[0].type
      }
    },
    tasksChangePage(page) {
      this.tasksCurrentPage = page
    },
    handleTaskEnter(task) {
      this.$utils.debounce(() => {
        this.task_hover_id = task.id
      }, 600)
    },
    handleTaskLeave() {
      this.task_hover_id = null
      this.$utils.debounce(() => {
        this.task_hover_id = null
      }, 600)
    },

    handleTaskSelected(task, close) {
      if (task.content) {
        task = task.content
        task.for_template = true
        if (task?.id) {
          task["id"] = this.$uuid()
        }
      }

      this.selected_tasks.unshift(task)
      this.tasksCurrentPage = 1
      if (this.selected_companies.length && !this.inPlaybook) {
        //this.assign_company_to_tasks_modal = true
      }
      close()
    },

    handleCreateTask() {
      this.$emitter.emit(ALL_EMITTERS.CREATE_TASK, {
        inGoal: true,
        records: this.selected_companies,
        is_all_records: this.isAllRecords,
        excluded_records: this.excludedRecords
      })
    },

    handleViewTask(task) {
      this.$emitter.emit(ALL_EMITTERS.UPDATE_TASK, { task: this.$utils.clone(task) })
    },

    handleRemoveTask(task) {
      let index = this.selected_tasks.findIndex((g) => g.id == task.id)
      this.selected_tasks.splice(index, 1)
      let index_2 = this.expanded_tasks.findIndex((task) => task == task.id)
      this.expanded_tasks.splice(index_2, 1)
      let index_3 = this.created_tasks.findIndex((t) => t.id == task.id)
      if (index_3 != -1) {
        this.$store.dispatch("data/removeTask", { task: this.created_tasks[index_3] })
        this.created_tasks.splice(index_3, 1)
      }
      const total_pages = Math.ceil(this.selected_tasks.length / this.tasksPerPage)

      if (this.tasksCurrentPage > total_pages) {
        this.tasksCurrentPage = total_pages
      }
    },

    handleExpandTask(task) {
      let index = this.expanded_tasks.findIndex((t) => t == task.id)
      if (index !== -1) {
        this.expanded_tasks.splice(index, 1)
      } else {
        this.expanded_tasks.push(task.id)
      }
    },

    focusStartDateInput() {
      this.inputStartDateIsFocused = !this.inputStartDateIsFocused
    },
    focusDueDateInput() {
      this.inputDueDateIsFocused = !this.inputDueDateIsFocused
    },
    clearDate() {
      this.$refs.start_date.hidePopover()
    },
    handleAssignConpanyToTasks(closeModal) {
      this.selected_tasks.map((task, index) => {
        let index_2 = this.created_tasks.findIndex((t) => t.id == task.id)
        if (index_2 != -1) {
          let post = { task: { id: task.id, record: this.selected_companies[0].id } }
          this.$store.dispatch("data/updateTask", post).then(() => {
            if (index == this.selected_tasks.length - 1) {
              closeModal()
            }
          })
        } else {
          delete task.assigned_to
          delete task.contacts
          task["record"] = this.selected_companies[0]
          let post = { task: task }
          this.$store.dispatch("data/bulkTaskCreate", { task: post }).then((res) => {
            this.created_tasks.push(res[0])
            let post = { task: { id: res[0].id, record: this.selected_companies[0].id } }
            this.$store.dispatch("data/updateTask", post).then((r) => {
              this.selected_tasks[index] = r
              if (index == this.selected_tasks.length - 1) {
                closeModal()
              }
            })
          })
        }
      })
    },
    createGoal(values) {
      let data = { ...values }
      data["description"] = this.goal_description
      data["assigned_to"] = this.selected_users.map((x) => (x.id ? x.id : x))
      data["contacts"] = this.selected_contacts.map((x) => (x.id ? x.id : x))
      data["tags"] = this.selected_tags.map((t) => t.id)

      data["auto_start_date_set"] = this.auto_set_start_date

      data["assign_companies_to_tasks"] = this.assign_companies_to_tasks
      data["assign_collaborators_to_tasks"] = this.assign_collaborators_to_tasks

      this.created_tasks = []
      data["tasks"] = []
      this.selected_tasks.map((t) => {
        if (t?.id && !t.for_template) {
          if (this.assign_companies_to_tasks || this.assign_collaborators_to_tasks) {
            let post = {
              task: { id: t.id }
            }
            if (this.assign_companies_to_tasks && this.selected_companies.length) {
              post["task"]["record"] = this.selected_companies[0].id
            }
            if (this.assign_collaborators_to_tasks && this.selected_users.length) {
              post["task"]["assigned_to"] = this.selected_users.map((x) => (x.id ? x.id : x))
            }
            this.$store.dispatch("data/updateTask", post)
          }
          data.tasks.push(t.id)
        } else {
          this.created_tasks.push(t)
        }
      })
      if (this.start_date) {
        data["start_date"] = this.$moment(this.start_date)
          .set("hour", this.$moment().hour())
          .set("minute", this.$moment().minute())
          .set("second", this.$moment().second())
          .toISOString()
      } else {
        data["start_date"] = null
      }
      if (this.relative_date) {
        data["due_date_relative_to"] = this.relative_date.due_date_relative_to
        data["due_date_days_offset"] = this.relative_date.due_date_days_offset
      } else if (this.due_date) {
        data["due_date"] = this.$moment(this.due_date)
          .set("hour", this.$moment().hour())
          .set("minute", this.$moment().minute())
          .set("second", this.$moment().second())
          .toISOString()
      } else {
        data["due_date"] = null
        data["due_date_relative_to"] = null
        data["due_date_days_offset"] = 0
      }

      this.submitting_is_on = true
      let post = null
      if (this.filter) {
        post = {
          goal: { ...data, filter: this.filter },
          records: this.selected_companies.map((r) => (r.id ? r.id : r)),
          is_all_records: this.isAllRecords,
          excluded_records: this.excludedRecords,
          object_type: this.objectType
        }
      } else {
        post = {
          goal: { ...data },
          records: this.selected_companies.map((r) => (r.id ? r.id : r)),
          is_all_records: this.isAllRecords,
          excluded_records: this.excludedRecords,
          object_type: this.objectType
        }
      }

      const params = { tasks_page_size: 6 }

      if (this.is_edit_template) {
        //Edit template
        this.selected_tasks.map((task) => {
          this.$store.dispatch("data/removeTask", { task: task })
          delete task["id"]
          delete task["assigned_to"]
          delete task["attachments"]
        })
        data["tasks"] = this.selected_tasks.map((task) => {
          return { ...task, record: task.record ? (task.record.id ? task.record.id : task.record) : null }
        })
        if (this.selected_companies.length) {
          data["record"] = this.selected_companies[0].id ? this.selected_companies[0].id : this.selected_companies[0]
        }
        let data_template = { ...this.selected_template.content, ...data }
        dataService
          .updateTemplate(this.selected_template.id, {
            name: data.name,
            content: data_template
          })
          .then((res) => {
            this.$emitter.emit(ALL_EMITTERS.TEMPLATE_UPDATED, res.data)
            this.$emit("done")
          })
      } else {
        //Create new goal
        data["tasks"] = this.selected_tasks.map((t) => t.id)

        if (this.inPlaybook) {
          data["companies"] = this.selected_companies.map((c) => (c.id ? c.id : c))

          if (this.filter) {
            data["filter"] = this.filter
          }

          data["assign_companies_to_tasks"] = this.assign_companies_to_tasks
          data["assign_collaborators_to_tasks"] = this.assign_collaborators_to_tasks
          data["status"] = "pending"

          const clean_tasks = []
          this.selected_tasks.forEach((t) => {
            const task_clone = this.$utils.clone(t)
            task_clone["assigned_to"] = task_clone["assigned_to"].map((t) => (t.value ? t.value : t.id ? t.id : t))
            task_clone["record"] = task_clone["record"]?.id ? task_clone["record"].id : task_clone["record"]
            clean_tasks.push(task_clone.id)
          })

          data["tasks"] = clean_tasks

          if (this.attachments.length) {
            const new_attachments = this.attachments.filter((att) => typeof att.file !== "string")
            const old_attachments = this.attachments.filter((att) => typeof att == "string")

            let attachment_calc = old_attachments

            if (new_attachments.length) {
              dataService.addAttachment({ attachments: this.attachments }).then((res) => {
                attachment_calc = attachment_calc.concat(res.data.map((a) => a.id))
                data["attachments"] = attachment_calc
                this.$emit("done", data)
              })
            } else {
              data["attachments"] = attachment_calc
              this.$emit("done", data)
            }
          } else {
            this.$emit("done", data)
          }
          google_event("create_goal_click", {
            event_label: "Create new goal",
            page: "Playbooks",
            name: "Playbooks",
            user_name: this.$store.state.auth.user.full_name,
            user_email: this.$store.state.auth.user.email,
            organization: this.$store.state.organization.name
          })
        } else {
          this.$store
            .dispatch("data/bulkGoalCreate", { goal: post, params })
            .then((res) => {
              if (this.isInPlan) {
                this.$emitter.emit(ALL_EMITTERS.ADD_SELECTED_GOALS_TO_PLAN_CREATE_FORM, res)
                this.$emitter.emit(ALL_EMITTERS.CONFIRM_ADD_GOAL_TASKS_TO_PLAN_UPDATE_FORM, res)
                this.$emitter.emit(ALL_EMITTERS.ADD_SELECTED_GOALS_TO_PLAN_UPDATE_FORM, res)
              }
              if (this.attachments.length) {
                let formData = new FormData()
                this.attachments.forEach((attachment, i) => {
                  Object.keys(attachment).forEach((field) => formData.append(`attachments[${i}][${field}]`, attachment[field]))
                })
                res.forEach((goal) => {
                  this.$store.dispatch("data/addAttachmentsToGoal", {
                    id: goal.id,
                    attachments: formData,
                    params
                  })
                })
              }
              let created_tasks_ids = []
              this.created_tasks.map((task) => {
                task["assigned_to"] = this.selected_users.map((x) => (x.id ? x.id : x)).filter((u) => u)
                delete task?.attachments
                delete task?.notes
                let post = { task: task }
                this.$store.dispatch("data/bulkTaskCreate", { task: post }).then((res2) => {
                  created_tasks_ids.push(res2[0].id)
                  if (created_tasks_ids.length == this.created_tasks.length) {
                    dataService.addTaskToGoal(res[0].id, { tasks_ids: created_tasks_ids })
                  }
                })
              })

              this.$emitter.emit(ALL_EMITTERS.NEW_ACTINON_CREATED_OR_UPDATED, this.due_date ? new Date(this.due_date) : null)
              this.$emit("done", res[0])
              if (this.isInMilestone) {
                this.$emitter.emit(ALL_EMITTERS.ADD_GOAL_TO_MILESTONE_CREATE_FORM, res[0])
              }
            })
            .catch(() => {
              // To do show exceptions
              this.$emit("error")
            })
        }
      }
    }
  }
}
</script>
